// SPDX-License-Identifier: AGPL-3.0-or-later

import * as GuildMemberCommands from '@app/features/member/commands/GuildMemberCommands';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {BanMemberModal} from '@app/features/moderation/components/modals/BanMemberModal';
import styles from '@app/features/moderation/components/profile/ModViewPanel.module.css';
import ModNotesStore from '@app/features/moderation/state/ModNotesStore';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import {
	ClockIcon,
	GavelIcon,
	NotePencilIcon,
	ShieldWarningIcon,
	SignOutIcon,
	TrashIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

interface ModViewPanelProps {
	user: User;
	profile?: Profile | null;
	guildId: string;
}

const TIMEOUT_DURATIONS = [
	{label: '60s', seconds: 60},
	{label: '5m', seconds: 300},
	{label: '10m', seconds: 600},
	{label: '1h', seconds: 3600},
	{label: '1d', seconds: 86400},
	{label: '1w', seconds: 604800},
];

export const ModViewPanel: React.FC<ModViewPanelProps> = observer(({user, guildId}) => {
	const member = GuildMembers.getMember(guildId, user.id);
	const isTimedOut = member ? member.isTimedOut() : false;
	const timeoutUntilDate = member?.communicationDisabledUntil
		? new Date(member.communicationDisabledUntil)
		: null;

	const [isUpdatingTimeout, setIsUpdatingTimeout] = useState(false);
	const [noteText, setNoteText] = useState('');
	const notes = ModNotesStore.getNotes(guildId, user.id);

	const handleApplyTimeout = async (seconds: number, label: string) => {
		setIsUpdatingTimeout(true);
		try {
			const until = new Date(Date.now() + seconds * 1000).toISOString();
			await GuildMemberCommands.timeout(guildId, user.id, until);
			ToastCommands.success(`Timed out ${user.username} for ${label}`);
		} catch {
			ToastCommands.error(`Failed to time out ${user.username}`);
		} finally {
			setIsUpdatingTimeout(false);
		}
	};

	const handleRemoveTimeout = async () => {
		setIsUpdatingTimeout(true);
		try {
			await GuildMemberCommands.timeout(guildId, user.id, null);
			ToastCommands.success(`Removed timeout for ${user.username}`);
		} catch {
			ToastCommands.error(`Failed to remove timeout`);
		} finally {
			setIsUpdatingTimeout(false);
		}
	};

	const handleKick = async () => {
		if (window.confirm(`Are you sure you want to kick ${user.username}?`)) {
			try {
				await GuildMemberCommands.kick(guildId, user.id);
				ToastCommands.success(`Kicked ${user.username}`);
			} catch {
				ToastCommands.error(`Failed to kick member`);
			}
		}
	};

	const handleOpenBanModal = () => {
		ModalCommands.push(
			modal(() => <BanMemberModal guildId={guildId} targetUser={user} />),
		);
	};

	const handleAddNote = (e: React.FormEvent) => {
		e.preventDefault();
		if (!noteText.trim()) return;
		ModNotesStore.addNote(guildId, user.id, noteText.trim());
		setNoteText('');
		ToastCommands.success('Added staff note');
	};

	const handleDeleteNote = (noteId: string) => {
		ModNotesStore.deleteNote(guildId, user.id, noteId);
	};

	return (
		<div className={styles.container}>
			{/* Quick Timeout Section */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<ClockIcon size={16} weight="bold" />
					<span>Quick Timeout</span>
				</div>

				<div className={styles.statusRow}>
					<span>Timeout Status:</span>
					{isTimedOut && timeoutUntilDate ? (
						<span className={`${styles.statusBadge} ${styles.statusTimedOut}`}>
							Timed out until {timeoutUntilDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
						</span>
					) : (
						<span className={`${styles.statusBadge} ${styles.statusClean}`}>
							No active timeout
						</span>
					)}
				</div>

				<div className={styles.durationGrid}>
					{TIMEOUT_DURATIONS.map((dur) => (
						<button
							key={dur.label}
							type="button"
							className={styles.durationButton}
							disabled={isUpdatingTimeout}
							onClick={() => handleApplyTimeout(dur.seconds, dur.label)}
						>
							{dur.label}
						</button>
					))}
				</div>

				{isTimedOut && (
					<button
						type="button"
						className={styles.removeTimeoutBtn}
						disabled={isUpdatingTimeout}
						onClick={handleRemoveTimeout}
					>
						Remove Timeout
					</button>
				)}
			</div>

			{/* Quick Moderation Actions */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<ShieldWarningIcon size={16} weight="bold" />
					<span>Enforcement</span>
				</div>
				<div className={styles.enforceRow}>
					<button type="button" className={styles.enforceButton} onClick={handleKick}>
						<SignOutIcon size={16} weight="bold" />
						<span>Kick Member</span>
					</button>
					<button type="button" className={styles.enforceButton} onClick={handleOpenBanModal}>
						<GavelIcon size={16} weight="bold" />
						<span>Ban Member</span>
					</button>
				</div>
			</div>

			{/* Staff Notes */}
			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<NotePencilIcon size={16} weight="bold" />
					<span>Moderator Staff Notes ({notes.length})</span>
				</div>

				<div className={styles.notesList}>
					{notes.length === 0 ? (
						<div className={styles.emptyNotes}>No staff notes on this member yet.</div>
					) : (
						notes.map((note) => (
							<div key={note.id} className={styles.noteCard}>
								<div className={noteHeaderClass}>
									<span className={styles.noteAuthor}>{note.authorTag}</span>
									<div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
										<span>{new Date(note.createdAt).toLocaleDateString()}</span>
										<button
											type="button"
											className={styles.deleteNoteBtn}
											onClick={() => handleDeleteNote(note.id)}
											title="Delete note"
										>
											<TrashIcon size={13} />
										</button>
									</div>
								</div>
								<div className={styles.noteText}>{note.text}</div>
							</div>
						))
					)}
				</div>

				<form className={styles.noteInputRow} onSubmit={handleAddNote}>
					<input
						type="text"
						className={styles.noteInput}
						placeholder="Add a private staff note about this member..."
						value={noteText}
						onChange={(e) => setNoteText(e.target.value)}
					/>
					<button type="submit" className={styles.addNoteBtn} disabled={!noteText.trim()}>
						Save Note
					</button>
				</form>
			</div>
		</div>
	);
});

const noteHeaderClass = styles.noteHeader;
